import { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';
import { site } from '../data/site';
import { projects } from '../data/projects';
import SectionHeader from '../ui/SectionHeader';
import MotionReveal from '../ui/MotionReveal';
import ProjectDetailModal from '../ui/ProjectDetailModal';
import {
  railStaggerContainer,
  railStaggerItem,
  usePrefersReducedMotion,
} from '../effects/motionConfig';

function splitTitle(title) {
  const parts = title.split(' — ');
  if (parts.length >= 2) {
    return { name: parts[0], subtitle: parts.slice(1).join(' — ') };
  }
  return { name: title, subtitle: '' };
}

function ProjectSlide({ project, onSelect, asMotion }) {
  const { name, subtitle } = splitTitle(project.title);

  const slide = (
    <button
      type="button"
      className="projects-rail__slide"
      onClick={() => onSelect(project)}
      aria-label={`View details for ${project.title}`}
    >
      <div className="projects-rail__media">
        {project.image ? (
          <img src={project.image} alt="" className="projects-rail__image" loading="lazy" />
        ) : (
          <div className="projects-rail__fallback" aria-hidden="true">{name.charAt(0)}</div>
        )}
        <div className="projects-rail__overlay" aria-hidden="true" />
        <div className="projects-rail__caption">
          <div className="projects-rail__caption-top">
            <h3 className="projects-rail__name">{name}</h3>
            {project.year && <span className="projects-rail__year">{project.year}</span>}
          </div>
          {subtitle && <p className="projects-rail__subtitle">{subtitle}</p>}
        </div>
      </div>
    </button>
  );

  if (!asMotion) {
    return <li className="projects-rail__item">{slide}</li>;
  }

  return (
    <motion.li className="projects-rail__item" variants={railStaggerItem}>
      {slide}
    </motion.li>
  );
}

export default function ProjectsSection() {
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [railProgress, setRailProgress] = useState(0);
  const railRef = useRef(null);
  const reduced = usePrefersReducedMotion();

  const scrollToSlide = useCallback((index) => {
    const rail = railRef.current;
    if (!rail) return;
    const slides = rail.querySelectorAll('.projects-rail__item');
    const target = slides[index];
    if (!target) return;
    target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', inline: 'center', block: 'nearest' });
    setActiveIndex(index);
  }, [reduced]);

  const scrollByDir = useCallback((dir) => {
    const next = Math.max(0, Math.min(projects.length - 1, activeIndex + dir));
    scrollToSlide(next);
  }, [activeIndex, scrollToSlide]);

  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return undefined;

    const onScroll = () => {
      const slides = [...rail.querySelectorAll('.projects-rail__item')];
      const center = rail.scrollLeft + rail.clientWidth / 2;
      let closest = 0;
      let minDist = Infinity;
      slides.forEach((slide, i) => {
        const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
        const dist = Math.abs(center - slideCenter);
        if (dist < minDist) {
          minDist = dist;
          closest = i;
        }
      });
      setActiveIndex(closest);

      const maxScroll = rail.scrollWidth - rail.clientWidth;
      setRailProgress(maxScroll > 0 ? rail.scrollLeft / maxScroll : 0);
    };

    onScroll();
    rail.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      rail.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      const rail = railRef.current;
      if (!rail) return;
      if (!rail.contains(document.activeElement) && document.activeElement !== document.body) return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        scrollByDir(1);
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        scrollByDir(-1);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [scrollByDir]);

  const railList = reduced ? (
    <ul ref={railRef} className="projects-rail__track" aria-label="Projects">
      {projects.map((project) => (
        <ProjectSlide
          key={project.title}
          project={project}
          onSelect={setSelectedProject}
          asMotion={false}
        />
      ))}
    </ul>
  ) : (
    <motion.ul
      ref={railRef}
      className="projects-rail__track"
      aria-label="Projects"
      variants={railStaggerContainer}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.08 }}
      tabIndex={0}
    >
      {projects.map((project) => (
        <ProjectSlide
          key={project.title}
          project={project}
          onSelect={setSelectedProject}
          asMotion
        />
      ))}
    </motion.ul>
  );

  return (
    <section id="projects" className="section projects-rail">
      <div className="container projects-rail__header">
        <MotionReveal>
          <SectionHeader title="Projects">
            <p>{site.projectsIntro}</p>
          </SectionHeader>
        </MotionReveal>
      </div>

      <div className="projects-rail__theater">
        <nav className="projects-rail__index" aria-label="Project list">
          {projects.map((project, index) => {
            const { name } = splitTitle(project.title);
            const isActive = index === activeIndex;
            return (
              <button
                key={project.title}
                type="button"
                className={`projects-rail__index-item ${isActive ? 'projects-rail__index-item--active' : ''}`}
                onClick={() => scrollToSlide(index)}
                aria-current={isActive ? 'true' : undefined}
              >
                <span className="projects-rail__index-name">{name}</span>
                {project.year && <span className="projects-rail__index-year">{project.year}</span>}
              </button>
            );
          })}
        </nav>

        <div className="projects-rail__stage">
          <div className="projects-rail__wrap">
            <button
              type="button"
              className="projects-rail__nav projects-rail__nav--prev"
              onClick={() => scrollByDir(-1)}
              aria-label="Previous project"
              disabled={activeIndex === 0}
            >
              ←
            </button>
            {railList}
            <button
              type="button"
              className="projects-rail__nav projects-rail__nav--next"
              onClick={() => scrollByDir(1)}
              aria-label="Next project"
              disabled={activeIndex === projects.length - 1}
            >
              →
            </button>
          </div>
          <div className="projects-rail__progress" aria-hidden="true">
            <div className="projects-rail__progress-fill" style={{ transform: `scaleX(${railProgress})` }} />
          </div>
        </div>
      </div>

      <ProjectDetailModal project={selectedProject} onClose={() => setSelectedProject(null)} />
    </section>
  );
}
